// THIRD-PARTY
import * as React from "react";
import cs from "classnames";
import PropTypes from "prop-types";

// UTILS
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";
import { getLocaleForContext } from "../../util/locale-utils";
import { useMicrocopySet } from "../use-microcopy/use-microcopy";

// COMPONENTS
import {
  Icon,
  ModalLink,
  RichText,
  baseRenderContentType,
} from "@lululemon/ui-library";
import { renderContentfulComponent } from "@lululemon/cosy-contentful-renderer";
import { footwearIcon } from "@lululemon/ui-icons";

// STYLES
import styles from "./footwear-returns-policy-modal.module.scss";

// Match what format we use in the resource key, default en_us
const getContentForLocale = (locale, content = {}) => {
  for (const value in content) {
    if (locale === value) {
      return content[value];
    }
  }
  return content?.en_us;
};

/**
 *
 * @param {Object} contentfulEntryId  the contentful entry id that we want to display. Defaults to the PDP 30-day with no pretext.
 * @param {string} locale Each 30-day modal has different version base don the locale
 * @param {{button: object, ptag: object}} classes "button" and "ptag" to apply CSS to the button or Pretext accordingly.
 * @returns {JSX.Element} a component that displays a call-to-action button with icon, OR a pretext and an in-line call-to-action.
 */
function FootwearReturnsPolicyModal({ type, locale, classes }) {
  const localeKey = (locale || getLocaleForContext())
    ?.toLowerCase()
    ?.replace("-", "_");

  const contentKey =
    type === "cdp"
      ? "footwear30DayPolicy.cdp.set"
      : "footwear30DayPolicy.pdp.set";
  const content = useMicrocopySet(contentKey);

  if (!content?.data) {
    return null;
  }

  const { pretext, cta, showIcon } = getContentForLocale(
    localeKey,
    content.get(".cta")
  );
  const modalBody = content.get(
    `footwear30DayPolicy.richText.body.${localeKey}`
  );

  const trackEventHandler = () => {
    trackEvent(EVENT_CATEGORY.GENERAL_COMPONENT_INTERACTION, {
      component: {
        id: "trial-pill",
        type: "ModalLink",
        text: "30-day trial",
        position: 1,
        totalCount: 1,
      },
    });
  };

  return (
    <span
      className={cs(styles.wrapper, classes?.wrapper)}
      data-testid="footwearReturnPolicyModal"
    >
      {showIcon && <Icon className={styles.icon} content={footwearIcon} />}
      <p className={styles.paragraph}>
        {pretext && <span>{pretext}&nbsp;</span>}
        <span
          role="link"
          tabIndex={0}
          data-testid="event-holder"
          onKeyDown={trackEventHandler}
          onClick={trackEventHandler}
        >
          <ModalLink
            linkText={cta}
            modalBody={
              <RichText
                customEntryRenderer={renderContentfulComponent}
                options={{ renderContentType: baseRenderContentType }}
                body={modalBody}
                className={styles.modalRichTextMargin}
              />
            }
            classes={{
              modalLinkContainer: cs(styles.modalButton, classes?.container),
              modalLinkText: cs(styles.ctaLabel, classes?.text),
            }}
          />
        </span>
      </p>
    </span>
  );
}

FootwearReturnsPolicyModal.propTypes = {
  classes: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  locale: PropTypes.string,
  type: PropTypes.string.isRequired,
};

FootwearReturnsPolicyModal.defaultProps = {
  classes: undefined,
  locale: "",
};

export default FootwearReturnsPolicyModal;
